import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import Container from 'components/Container';
import Row from 'components/Row';
import Col from 'components/Col';
import AddNewIcon from 'assets/icons/add-new';
import Accordion from 'components/Accordion';
import Modal from 'components/Modal';
import { SITE_STATUS_DRAFT_ID, SITE_STATUS_EDIT_ONLY_ID, SITE_STATUS_SUBMITTED_EDIT_ONLY_ID } from 'constants/siteStatuses';
import HabitatsHeader from './HabitatsHeader';
import { AddNewHabitatsButton } from '../style';
import HabitatsContent from './HabitatContent';
import HabitatsContentHeader from './HabitatsContentHeader';
import { HabitatContext } from 'providers/HabitatContext';
import RemoveHabitatModal from './RemoveHabitatModal';
import {
  requestCreateSiteHabitat,
  requestDeleteSiteHabitat,
  requestGetSiteHabitats,
  requestUpdateSiteHabitat
} from 'api/siteHabitat';

const defaultHabitat = {
  name: 'default',
  Level1HabitatId: null,
  Level2HabitatId: null,
  Level3HabitatId: null,
  percentage: 0,
  id: ''
};

const Habitats = ({ site, disabled }) => {
  const { defaultHabitatsOptions, filterAvailableHabitatsOptions } = useContext(HabitatContext);
  const [habitatsList, setHabitatsList] = useState([]);
  const options = filterAvailableHabitatsOptions(habitatsList);
  const [habitatForDelete, setHabitatForDelete] = useState({});
  const params = useParams();
  const [rerenderFlag, setRerenderFlag] = useState(false);
  const [uniqueHabitats, setUniqueHabitats] = useState([]);

  const editOnlySiteStatus = site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID || site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  const historyId = editOnlySiteStatus ? site.sitehistoryId : params.siteHistoryId
  const siteId = editOnlySiteStatus ? site.siteId : site.id

  // Fetch pre-existing habitats when the component mounts
  useEffect(() => {
    if (site.SiteHabitats == undefined || site.SiteHabitats.length == 0) {
      getHabitats();
    } else {
      // If habitat data is already available, set it in the state
      setHabitatsList(site.SiteHabitats.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)));
    }
  }, [site.SiteHabitats, params.siteHistoryId]);

  const getHabitats = () => {
    requestGetSiteHabitats(siteId, historyId).then((data) => {
      const sorted = data.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
      setHabitatsList(sorted);
    });
  };

  // Function to force component rerender
  const forceRerender = () => {
    setRerenderFlag(prev => !prev);
  };

  // Update uniqueHabitats whenever habitatsList changes
  useEffect(() => {
    // Filter out duplicates based on habitat IDs
    const uniqueHabitats = habitatsList.filter((habitat, index, self) =>
      index === self.findIndex((h) => (
        h.Level1HabitatId === habitat.Level1HabitatId &&
        h.Level2HabitatId === habitat.Level2HabitatId &&
        h.Level3HabitatId === habitat.Level3HabitatId
      ))
    );
    setUniqueHabitats(uniqueHabitats);
  }, [habitatsList]);


  const updateHabitats = (habitatId, param) => {
    if (!param || !habitatId) {
      return;
    }
    requestUpdateSiteHabitat(habitatId, params.siteHistoryId, param).then(() => {
      getHabitats();
    });
  };

  const createHabitats = (newHabitats) => {
    // Check if the new habitat already exists in the habitatsList
    const isDuplicate = habitatsList.some(habitat => {
      return (
        habitat.Level1HabitatId === newHabitats.Level1HabitatId &&
        habitat.Level2HabitatId === newHabitats.Level2HabitatId &&
        habitat.Level3HabitatId === newHabitats.Level3HabitatId
      );
    });

    // If habitat already exists, do not proceed with creation
    if (isDuplicate) {
      return;
    }

    // If habitat doesn't exist, proceed with creation
    requestCreateSiteHabitat(site.id, params.siteHistoryId, newHabitats).then(() => {
      getHabitats();
    });
  };

  const handleOpenRemoveModal = (habitat) => {
    setHabitatForDelete(habitat);
  };

  const handleConfirmRemove = () => {
    deleteHabitats(habitatForDelete.id);
    setHabitatForDelete({});
    forceRerender(); // Trigger rerender after confirming deletion
  };


  const deleteHabitats = (id) => {
    if (!id) {
      return;
    }


    requestDeleteSiteHabitat(id, params.siteHistoryId)
      .then(() => {
        setHabitatsList(prevHabitats => {
          const updatedHabitats = prevHabitats.filter(habitat => habitat.id !== id);
          return updatedHabitats;
        });
      })
      .catch(error => {
      });
  };


  const handleAddHabitats = () => {
    const availableToAddNewHabitats = habitatsList.every((element) => element.id !== '');

    if (!availableToAddNewHabitats) {
      return;
    }

    setHabitatsList((s) => [...s, defaultHabitat]);
  };

  return (
    <Container>
      <Row>
        <Col md={7} mdOffset={12}>
          <HabitatsHeader marginBottom='24px' label='whatHabitatsAreYouAssessingForThisSite' subTitleHint='habitatsHint' />
        </Col>
      </Row>
      <Row>
        <Col md={9} mdOffset={2}>
          {/* eslint-disable-next-line no-magic-numbers */}
          {uniqueHabitats.length === 0 ? (
            <HabitatsContent
              item={defaultHabitat}
              options={options}
              createHabitats={createHabitats}
              defaultHabitatsOptions={defaultHabitatsOptions}
              updateHabitats={updateHabitats}
              site={site}
              disabled={disabled}
            />
          ) : (
            <Accordion
              contentComponent={HabitatsContent}
              headerComponent={HabitatsContentHeader}
              headerProps={{ handleOpenRemoveModal, disabled }}
              contentProps={{ options, createHabitats, updateHabitats, defaultHabitatsOptions, site, disabled }}
              items={uniqueHabitats}
            />
          )}
        </Col>
      </Row>
      {!(site.SiteStatusId !== SITE_STATUS_DRAFT_ID) && (
        <Row>
          <Col md={8} mdOffset={2}>
            <AddNewHabitatsButton onClick={handleAddHabitats} disabled={disabled}>
              <AddNewIcon />
              <FormattedMessage id='newHabitat' />
            </AddNewHabitatsButton>
          </Col>
        </Row>
      )}
      <Modal
        width={'416px'}
        isOpen={Boolean(habitatForDelete.id)}
        toggleModal={() => {
          setHabitatForDelete({});
        }}
        modalTitleId='removeHabitat'
        bottomBorder={true}
      >
        <RemoveHabitatModal
          item={habitatForDelete}
          habitatsOptions={defaultHabitatsOptions}
          closeModal={() => {
            setHabitatForDelete({});
          }}
          confirmRemove={handleConfirmRemove}
        />
      </Modal>
    </Container>
  );
};

export default Habitats;